import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Skeleton } from '@bloomcode/ui';
import { ArrowLeft, Download, CreditCard } from '@bloomcode/icons';
import { useInvoiceDetail } from '@/hooks';

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR' }).format(
    amount / 100
  );
}

function statusBadgeVariant(status: string): 'default' | 'success' | 'destructive' | 'warning' {
  switch (status) {
    case 'PAID':
      return 'success';
    case 'OVERDUE':
      return 'destructive';
    case 'OPEN':
      return 'warning';
    default:
      return 'default';
  }
}

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { invoice, loading, error } = useInvoiceDetail(id);

  const canPay = invoice?.status === 'OPEN' || invoice?.status === 'OVERDUE';

  const handleDownload = () => {
    if (invoice?.invoice_url) {
      window.open(invoice.invoice_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handlePayNow = () => {
    // Navigate to payments page — actual payment flow handled by payment provider
    navigate('/payments');
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-destructive py-16">
          Failed to load invoice. Please try again.
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {invoice ? `Invoice ${invoice.invoice_number}` : 'Invoice'} | Bloomcode Ledger Portal
        </title>
        <meta name="description" content="View invoice details, line items, and payment status." />
      </Helmet>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Back Button */}
        <Button variant="ghost" size="sm" className="mb-6 gap-2" onClick={() => navigate('/invoices')}>
          <ArrowLeft className="h-4 w-4" />
          Back to Invoices
        </Button>

        {/* Invoice Header */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                {loading ? (
                  <>
                    <Skeleton className="h-7 w-48 mb-2" />
                    <Skeleton className="h-4 w-32" />
                  </>
                ) : (
                  <>
                    <CardTitle className="text-xl font-[Manrope]">
                      Invoice {invoice?.invoice_number}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Issued:{' '}
                      {invoice?.created_at
                        ? new Date(invoice.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })
                        : '—'}
                    </p>
                    {invoice?.due_date && (
                      <p className="text-sm text-muted-foreground">
                        Due:{' '}
                        {new Date(invoice.due_date).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </p>
                    )}
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                {loading ? (
                  <Skeleton className="h-6 w-16" />
                ) : (
                  invoice && (
                    <Badge variant={statusBadgeVariant(invoice.status)}>{invoice.status}</Badge>
                  )
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={loading || !invoice?.invoice_url}
                onClick={handleDownload}
              >
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
              {canPay && (
                <Button size="sm" className="gap-2" onClick={handlePayNow}>
                  <CreditCard className="h-4 w-4" />
                  Pay Now
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Totals */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Summary</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex justify-between">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                ))}
              </div>
            ) : invoice ? (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatCurrency(invoice.subtotal, invoice.currency)}</span>
                </div>
                {invoice.tax != null && invoice.tax > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax</span>
                    <span>{formatCurrency(invoice.tax, invoice.currency)}</span>
                  </div>
                )}
                {invoice.discount_amount != null && invoice.discount_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Discount</span>
                    <span className="text-success">
                      -{formatCurrency(invoice.discount_amount, invoice.currency)}
                    </span>
                  </div>
                )}
                <div className="border-t pt-2 flex justify-between font-semibold">
                  <span>Total</span>
                  <span>{formatCurrency(invoice.total, invoice.currency)}</span>
                </div>
                {invoice.amount_paid > 0 && (
                  <div className="flex justify-between text-sm text-success">
                    <span>Amount Paid</span>
                    <span>{formatCurrency(invoice.amount_paid, invoice.currency)}</span>
                  </div>
                )}
                {invoice.amount_remaining > 0 && (
                  <div className="flex justify-between text-sm font-medium text-destructive">
                    <span>Amount Due</span>
                    <span>{formatCurrency(invoice.amount_remaining, invoice.currency)}</span>
                  </div>
                )}
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
